/**
 * Dashboard skeleton loader component
 * Shows placeholder structure while dashboard data is loading
 */

import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

export function DashboardSkeleton() {
  return (
    <div className="space-y-6" data-testid="dashboard-skeleton">
      {/* TOP ROW – STAT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-8 w-16 mb-1" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-8 w-8 rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* BOTTOM ROW – CASH FLOW STATS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-20 mb-1" />
                  <Skeleton className="h-3 w-28" />
                </div>
                <Skeleton className="h-8 w-8 rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* QUICK ACTIONS */}
      <div className="flex items-center gap-3 flex-wrap">
        <Skeleton className="h-10 w-24" />
        <Skeleton className="h-10 w-28" />
        <Skeleton className="h-10 w-32" />
      </div>

      {/* CALENDAR SECTION */}
      <Card>
        <CardHeader className="p-4 border-b bg-gray-50 dark:bg-gray-800">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <Skeleton className="h-7 w-48" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-9" />
              <Skeleton className="h-9 w-16" />
              <Skeleton className="h-9 w-9" />
              <div className="flex gap-1 ml-2">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-8 w-16" />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <Skeleton className="h-[700px] w-full rounded" />
        </CardContent>
      </Card>

      {/* STAFF SNAPSHOT & QUICK STATS */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Team Card */}
        <Card>
          <CardHeader className="p-4 border-b">
            <Skeleton className="h-6 w-16" />
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center justify-between p-2">
                  <div className="flex-1">
                    <Skeleton className="h-4 w-24 mb-1" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <Skeleton className="h-2 w-2 rounded-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Metric Cards */}
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="p-4 border-b">
              <Skeleton className="h-6 w-20" />
            </CardHeader>
            <CardContent className="p-4">
              <div className="text-center">
                <Skeleton className="h-9 w-12 mx-auto mb-1" />
                <Skeleton className="h-3 w-24 mx-auto" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* RECENT ACTIVITY SECTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((section) => (
          <Card key={section}>
            <CardHeader className="p-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-16" />
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-2">
                {[1, 2, 3, 4].map((item) => (
                  <div key={item} className="p-3 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-16 rounded" />
                    </div>
                    <Skeleton className="h-3 w-32 mb-1" />
                    <Skeleton className="h-3 w-40" />
                    <div className="flex items-center justify-between mt-2 pt-2 border-t">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

